import React, { useEffect, useState, useRef } from 'react';
import validator from 'validator';
import { useImmer } from 'use-immer';
import { Typography } from '../typography';
import { Button } from '../button';
import { FileInput, Input, InputNumber, InputPrice } from '../input';
import {
  ModalBase,
  ModalWrapper,
  ModalFooterButtons,
  ModalBodyCustomColumns,
} from './ModalComponents';
import { enqueueSnackbar } from '../snackbar';
import {
  useConvertCurrencyFcQuery,
  useSetPriceRepairMutation,
  useStoragePricingCurrencyCodeQuery,
  useUpdateImagesRepairMutation,
  useUpdateRepairMutation,
} from '../../hooks';
import { CURRENCIES, currencyOptions, INPUT_ERRORS, MESSAGES } from '../../const';
import { CurrencyCode, ImageObjType, RepairUpdateImagesProps } from '../../types';
import { Select } from '../select';
import { ModalImages } from './UploadImagesModal';
import { fullScreenButtonClick, HideInput, ImageGallery, ImageGalleryCard } from '../image-gallery';
import { filesFormatValidator } from '../../utils';
import { BorderLine } from '../border-line';
import { i18n } from '../../i18n';

interface RepairSetUpdateModalType {
  isSubmitClicked: boolean;
  submitClick: () => void;
  open: () => void;
  name: string;
  action: 'set-price' | 'edit';

  number: string;
  data: {
    id: string;
    price: string;
    currencyCode?: CurrencyCode;
    expectedDurationDays: number;
    description: string;
    beforeImages: { url: string; name: string }[];
    afterImages?: { url: string; name: string }[];
  }[];
}

export const repairSetUpdateModal: RepairSetUpdateModalType = {
  isSubmitClicked: false,
  submitClick: () => null,
  open: () => null,
  name: '',
  action: 'set-price',

  number: '',
  data: [],
};

export const RepairSetUpdateModal = () => {
  const setPriceMutation = useSetPriceRepairMutation();
  const updateMutation = useUpdateRepairMutation();
  const updateImagesRepairMutation = useUpdateImagesRepairMutation();
  const convertCurrency = useConvertCurrencyFcQuery();
  useStoragePricingCurrencyCodeQuery(
    repairSetUpdateModal.data[0]?.currencyCode ? '' : repairSetUpdateModal.data[0]?.id,
    (currencyCode) => {
      if (repairSetUpdateModal.data[0]?.currencyCode) return;
      setCurrencyCode([currencyCode]);
    },
  );

  const [open, setOpen] = useState(false);

  const [price, setPrice] = useState(['0']);
  const [currencyCode, setCurrencyCode] = useState<CurrencyCode[]>(['USD']);
  const [expectedDurationDays, setExpectedDurationDays] = useState([0]);
  const [description, setDescription] = useState(['']);
  const [zoomImageIndex, setZoomImageIndex] = useState(0);
  const [imageGalleryPhotos, setImageGalleryPhotos] = useState<any[]>([]);

  const [oldBeforeImages, setOldBeforeImages] = useImmer<
    RepairSetUpdateModalType['data'][number]['beforeImages'][]
  >([[]]);
  const deleteOldBeforeImages = useRef<any[][]>([[]]);
  const [newBeforeImages, setNewBeforeImages] = useImmer<any[][]>([[]]);
  const [newBeforeImagesFiles, setNewBeforeImagesFiles] = useImmer<any[][]>([[]]);
  const beforeImagesInputRef = useRef<any>([]);

  const [oldAfterImages, setOldAfterImages] = useImmer<
    NonNullable<RepairSetUpdateModalType['data'][number]['afterImages']>[]
  >([[]]);
  const deleteOldAfterImages = useRef<any[][]>([[]]);
  const [newAfterImages, setNewAfterImages] = useImmer<any[][]>([[]]);
  const [newAfterImagesFiles, setNewAfterImagesFiles] = useImmer<any[][]>([[]]);
  const afterImagesInputRef = useRef<any>([]);

  const [updateKey, setUpdateKey] = useState<any>();

  const validators = price.map((_, i) => ({
    price: !String(price[i]).length
      ? INPUT_ERRORS.REQUIRED.PRICE
      : Number(price[i]) <= 0
      ? INPUT_ERRORS.NOT_VALID.PRICE_MIN_VALUE
      : '',
    expectedDurationDays: !String(expectedDurationDays[i]).length
      ? INPUT_ERRORS.REQUIRED.DAYS_TO_COMPLETE
      : Number(expectedDurationDays[i]) <= 0
      ? INPUT_ERRORS.NOT_VALID.DAYS_TO_COMPLETE_MIN_VALUE
      : '',
    description: !description[i]
      ? INPUT_ERRORS.REQUIRED.DESCRIPTION
      : !validator.isLength(description[i], { min: 10, max: 500 })
      ? INPUT_ERRORS.NOT_VALID.REPAIR_DESCRIPTION_LENGTH
      : '',
    beforeImages: ![...oldBeforeImages[i], ...newBeforeImages[i]].length
      ? INPUT_ERRORS.REQUIRED.IMAGES
      : '',
  }));

  useEffect(() => {
    repairSetUpdateModal.open = async () => {
      const names = {
        'set-price': i18n.t('pages.modal.repairAdminApprovedTitle'),
        edit: i18n.t('pages.modal.repairUpdateTitle'),
        repair: i18n.t('pages.modal.repairTitle'),
      };
      repairSetUpdateModal.name = names[repairSetUpdateModal.action];
      repairSetUpdateModal.name = `${repairSetUpdateModal.name}: ${repairSetUpdateModal.number}`;

      if (repairSetUpdateModal.data[0].currencyCode) {
        setCurrencyCode([repairSetUpdateModal.data[0].currencyCode]);
      }

      setPrice([repairSetUpdateModal.data[0].price]);
      setExpectedDurationDays([repairSetUpdateModal.data[0].expectedDurationDays]);
      setDescription([repairSetUpdateModal.data[0].description]);
      setOldBeforeImages([repairSetUpdateModal.data[0].beforeImages]);
      setOldAfterImages([repairSetUpdateModal.data[0].afterImages || []]);

      setNewBeforeImages([[]]);
      setNewBeforeImagesFiles([[]]);
      setNewAfterImages([[]]);
      setNewAfterImagesFiles([[]]);
      deleteOldBeforeImages.current = [[]];
      deleteOldAfterImages.current = [[]];

      repairSetUpdateModal.isSubmitClicked = false;
      setOpen(true);
    };
  }, []);

  const handleCurrencyChange = (e: any, i: number) => {
    const oldCurrencyCode = currencyCode[i];
    const newCurrencyCode = e.target.value;

    const convertedValue = convertCurrency(oldCurrencyCode, newCurrencyCode, Number(price));

    price[i] = convertedValue.toFixed(2);
    setPrice(price);

    currencyCode[i] = newCurrencyCode;
    setCurrencyCode(currencyCode);
    setUpdateKey(Math.random());
  };

  const handleDeleteImageClick = (
    i: number,
    n: number,
    imageCategory: RepairUpdateImagesProps['action'],
  ) => {
    if (imageCategory === 'before') {
      const isDeleteNewImage = n - oldBeforeImages[i].length >= 0;

      if (isDeleteNewImage) {
        n = n - oldBeforeImages[i].length;

        setNewBeforeImages((props) => {
          props[i] = props[i].filter((_: any, index: number) => index !== n);
        });
        setNewBeforeImagesFiles((props) => {
          props[i] = props[i].filter((_: any, index: number) => index !== n);
        });
      } else {
        deleteOldBeforeImages.current[i] = [
          ...deleteOldBeforeImages.current[i],
          { action: 'delete', name: oldBeforeImages[i][n].name },
        ];

        setOldBeforeImages((props) => {
          props[i] = props[i].filter((_: any, index: number) => index !== n);
        });
      }

      return;
    }

    const isDeleteNewImage = n - oldAfterImages[i].length >= 0;

    if (isDeleteNewImage) {
      n = n - oldAfterImages[i].length;

      setNewAfterImages((props) => {
        props[i] = props[i].filter((_: any, index: number) => index !== n);
      });
      setNewAfterImagesFiles((props) => {
        props[i] = props[i].filter((_: any, index: number) => index !== n);
      });
    } else {
      deleteOldAfterImages.current[i] = [
        ...deleteOldAfterImages.current[i],
        { action: 'delete', name: oldAfterImages[i][n].name },
      ];

      setOldAfterImages((props) => {
        props[i] = props[i].filter((_: any, index: number) => index !== n);
      });
    }
  };

  const handleZoomClick = (
    i: number,
    n: number,
    imageCategory: RepairUpdateImagesProps['action'],
  ) => {
    const photos =
      imageCategory === 'before'
        ? [...oldBeforeImages[i].map(({ url }: any) => url), ...newBeforeImages[i]]
        : [...oldAfterImages[i].map(({ url }: any) => url), ...newAfterImages[i]];

    setImageGalleryPhotos(photos);
    setZoomImageIndex(n);
    fullScreenButtonClick('set-update');
  };

  const handleChangeFiles = (
    newImages: any,
    newFiles: any,
    i: number,
    imageCategory: RepairUpdateImagesProps['action'],
  ) => {
    if (filesFormatValidator(newFiles, 'Image')) {
      enqueueSnackbar(filesFormatValidator(newFiles, 'Image'), 'warning');
      return;
    }

    if (imageCategory === 'before') {
      setNewBeforeImages((props) => {
        props[i] = [...props[i], ...newImages];
      });

      setNewBeforeImagesFiles((props) => {
        props[i] = [...props[i], ...newFiles];
      });

      beforeImagesInputRef.current[i].type = 'text';
      beforeImagesInputRef.current[i].type = 'file';
      return;
    }

    setNewAfterImages((props) => {
      props[i] = [...props[i], ...newImages];
    });

    setNewAfterImagesFiles((props) => {
      props[i] = [...props[i], ...newFiles];
    });

    afterImagesInputRef.current[i].type = 'text';
    afterImagesInputRef.current[i].type = 'file';
  };

  const handleOnClose = () => {
    repairSetUpdateModal.data = [];
    setOpen(!open);
  };

  const handleSubmitClick = async () => {
    repairSetUpdateModal.isSubmitClicked = true;
    setUpdateKey(Math.random());

    if (newBeforeImages.some((item) => item.length > 10)) {
      enqueueSnackbar(INPUT_ERRORS.NOT_VALID.MAX_UPLOAD_FILES, 'warning');
      return;
    }

    if (newAfterImages.some((item) => item.length > 10)) {
      enqueueSnackbar(INPUT_ERRORS.NOT_VALID.MAX_UPLOAD_FILES, 'warning');
      return;
    }

    if (validators.some((v) => Object.values(v).some((field) => !!field))) {
      return;
    }

    const beforeImagesData = price.map((_, n) =>
      newBeforeImages[n].map((_: any, i: any) => ({
        action: 'upload' as ImageObjType['action'],
        file: newBeforeImagesFiles[n][i],
      })),
    );

    const afterImagesData = price.map((_, n) =>
      newAfterImages[n].map((_: any, i: any) => ({
        action: 'upload' as ImageObjType['action'],
        file: newAfterImagesFiles[n][i],
      })),
    );

    const updateImageMutate = (repairIds: string[]) => {
      const finish = () => {
        setOpen(false);
        repairSetUpdateModal.submitClick();
      };

      const updateAfterImages = (repairId: string, i: number) => {
        const afterPhotos = [...afterImagesData[i], ...deleteOldAfterImages.current[i]];

        if (!afterPhotos.length) {
          finish();
          return;
        }

        updateImagesRepairMutation.mutate(
          {
            id: repairId,
            photos: afterPhotos,
            action: 'after',
          },
          { onSuccess: finish },
        );
      };

      repairIds.forEach((id, i) =>
        updateImagesRepairMutation.mutate(
          {
            id,
            photos: [...beforeImagesData[i], ...deleteOldBeforeImages.current[i]],
            action: 'before',
          },
          {
            onSuccess: () => updateAfterImages(id, i),
          },
        ),
      );
    };

    const mutateData = price.map((_, i: number) => ({
      id: repairSetUpdateModal.data[0].id,
      price: Number(price[i]),
      currencyCode: currencyCode[i],
      expectedDurationDays: expectedDurationDays[i],
      description: description[i],
    }));

    const mutate: any =
      repairSetUpdateModal.action === 'edit' ? updateMutation.mutate : setPriceMutation.mutate;

    mutate(repairSetUpdateModal.action === 'edit' ? mutateData[0] : mutateData, {
      onSuccess: (res: any) =>
        updateImageMutate(
          repairSetUpdateModal.action === 'edit' ? [repairSetUpdateModal.data[0].id] : res.data.ids,
        ),
      onError: () => {
        enqueueSnackbar(MESSAGES.REPAIR.ERROR.NOT_SET, 'error');
      },
    });
  };

  return (
    <>
      <ModalBase id={'repair-set-update-modal'} open={open} onClose={handleOnClose}>
        <ModalWrapper>
          <Typography variant={'textL'} weight={'bold'}>
            {repairSetUpdateModal.name}
          </Typography>
          {price.map((_, i: number) => (
            <React.Fragment key={i + updateKey}>
              <ModalBodyCustomColumns columns={'1fr 1fr 1fr'}>
                <InputPrice
                  label={i18n.t('label.price') + '*'}
                  data-size={'medium'}
                  defaultValue={price[i]}
                  onChange={(e) => {
                    const newPrice = [...price];
                    newPrice[i] = e.target.value;
                    setPrice(newPrice);
                  }}
                  endIcon={currencyCode[i] ? CURRENCIES[currencyCode[i]].symbol : ''}
                  error={repairSetUpdateModal.isSubmitClicked && !!validators[i].price}
                  helperText={repairSetUpdateModal.isSubmitClicked ? validators[i].price : ''}
                />
                <Select
                  label={i18n.t('label.currencyCode')}
                  options={currencyOptions}
                  value={currencyCode[i]}
                  onChange={(e) => handleCurrencyChange(e, i)}
                />
                <InputNumber
                  label={i18n.t('label.daysToComplete') + '*'}
                  placeholder={i18n.t('placeholder.days')}
                  data-size={'medium'}
                  defaultValue={expectedDurationDays[i]}
                  onChange={(e) => {
                    const newDays = [...expectedDurationDays];
                    newDays[i] = Number(e.target.value);
                    setExpectedDurationDays(newDays);
                  }}
                  error={
                    repairSetUpdateModal.isSubmitClicked && !!validators[i].expectedDurationDays
                  }
                  helperText={
                    repairSetUpdateModal.isSubmitClicked ? validators[i].expectedDurationDays : ''
                  }
                />
              </ModalBodyCustomColumns>
              <Input
                label={i18n.t('label.description') + '*'}
                data-size={'medium'}
                defaultValue={description[i]}
                onChange={(e) => {
                  const newDescription = [...description];
                  newDescription[i] = e.target.value;
                  setDescription(newDescription);
                }}
                error={repairSetUpdateModal.isSubmitClicked && !!validators[i].description}
                helperText={repairSetUpdateModal.isSubmitClicked ? validators[i].description : ''}
                rows={4}
                multiline
              />
              <ModalBodyCustomColumns columns={'max-content 1fr max-content'}>
                <Typography
                  variant={'textM'}
                  weight={'bold'}
                  color={
                    repairSetUpdateModal.isSubmitClicked && validators[i].beforeImages
                      ? '--red-100'
                      : '--gray-100'
                  }
                >
                  {i18n.t('label.beforeImages')}*
                </Typography>
                <span />
                <Button
                  size={'medium'}
                  data-color={'primary'}
                  onClick={() => beforeImagesInputRef.current[0].click()}
                  justify={'left'}
                >
                  {i18n.t('button.addBeforeImages')}
                </Button>
              </ModalBodyCustomColumns>
              <ModalImages isVisible>
                {[...oldBeforeImages[i], ...newBeforeImages[i]].map((img, n) => (
                  <ImageGalleryCard
                    key={n}
                    img={typeof img === 'string' ? img : img.url}
                    handleZoomClick={() => handleZoomClick(i, n, 'before')}
                    handleDeleteImageClick={() => handleDeleteImageClick(i, n, 'before')}
                  />
                ))}
                <HideInput>
                  <FileInput
                    id={'file-input-before'}
                    label={i18n.t('label.beforeImages')}
                    inputRef={(ref) => {
                      if (ref) {
                        beforeImagesInputRef.current[i] = ref;
                      }
                    }}
                    inputProps={{
                      accept: '.jpeg, .jpg, .png, .webp',
                      multiple: true,
                    }}
                    handleChangeFiles={(newImages, newFiles) =>
                      handleChangeFiles(newImages, newFiles, i, 'before')
                    }
                  />
                </HideInput>
              </ModalImages>
              <BorderLine space={'none'} />
              <ModalBodyCustomColumns columns={'max-content 1fr max-content'}>
                <Typography variant={'textM'} weight={'bold'}>
                  {i18n.t('label.afterImages')}
                </Typography>
                <span />
                <Button
                  size={'medium'}
                  data-color={'primary'}
                  onClick={() => afterImagesInputRef.current[0]?.click()}
                  justify={'left'}
                >
                  {i18n.t('button.addAfterImages')}
                </Button>
              </ModalBodyCustomColumns>
              <ModalImages isVisible>
                {[...oldAfterImages[i], ...newAfterImages[i]].map((img, n) => (
                  <ImageGalleryCard
                    key={n}
                    img={typeof img === 'string' ? img : img.url}
                    handleZoomClick={() => handleZoomClick(i, n, 'after')}
                    handleDeleteImageClick={() => handleDeleteImageClick(i, n, 'after')}
                  />
                ))}
                <HideInput>
                  <FileInput
                    id={'file-input-after'}
                    label={i18n.t('label.afterImages')}
                    inputRef={(ref) => {
                      if (ref) {
                        afterImagesInputRef.current[i] = ref;
                      }
                    }}
                    inputProps={{
                      accept: '.jpeg, .jpg, .png, .webp',
                      multiple: true,
                    }}
                    handleChangeFiles={(newImages, newFiles) =>
                      handleChangeFiles(newImages, newFiles, i, 'after')
                    }
                  />
                </HideInput>
              </ModalImages>
            </React.Fragment>
          ))}
          <ModalFooterButtons columns={'max-content'}>
            <Button size={'medium'} data-color={'white'} onClick={handleOnClose}>
              {i18n.t('button.cancel')}
            </Button>
            <Button size={'medium'} data-color={'primary'} onClick={handleSubmitClick}>
              {i18n.t('button.submit')}
            </Button>
          </ModalFooterButtons>
        </ModalWrapper>
      </ModalBase>
      <ImageGallery id={'set-update'} photos={imageGalleryPhotos} startIndex={zoomImageIndex} />
    </>
  );
};
